<template>
  <div>
    <Head :title="$t('models.candidate')"/>

    <div class="flex justify-start mb-8">
      <h1 class="text-3xl font-bold">
        <Link class="text-indigo-400 hover:text-indigo-600" :href="route('admin.elections.index')">
          {{ $t('models.election') }}
        </Link>
        <span class="text-indigo-400 font-medium mx-2">/</span>
        <Link class="text-indigo-400 hover:text-indigo-600"
              :href="route('admin.elections.edit', election.uuid)">
          {{ election.name }}
        </Link>
        <span class="text-indigo-400 font-medium mx-2">/</span>
        {{ $t('models.candidate') }}
      </h1>

      <div class="ml-auto">
        <Link class="inline-block btn-indigo" :href="route('admin.elections.edit', election.uuid)">
          <span>{{ $t('ui.back') }}</span>
        </Link>
      </div>
    </div>

    <div class="bg-white rounded-md shadow overflow-hidden mb-8">
      <div class="flex flex-wrap -mb-8 -mr-6 p-8">
        <text-input v-model="election.name" class="pb-8 pr-6 w-full lg:w-1/3"
                    :label="$t('fields.election.name')" disabled/>
        <text-input v-model="election.datetime_start" class="pb-8 pr-6 w-full md:w-1/2 lg:w-1/3"
                    :label="$t('fields.election.datetime_start')" disabled/>
        <text-input v-model="election.datetime_end" class="pb-8 pr-6 w-full md:w-1/2 lg:w-1/3"
                    :label="$t('fields.election.datetime_end')" disabled/>
        <text-input v-model="election.candidates_count" class="pb-8 pr-6 w-full md:w-1/2 lg:w-1/3"
                    :label="$t('fields.election.candidates_count')" disabled/>
        <text-input v-model="election.ballots_count" class="pb-8 pr-6 w-full md:w-1/2 lg:w-1/3"
                    :label="$t('fields.election.ballots_count')" disabled/>
      </div>
    </div>

    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset">
        <label class="block mt-4 text-gray-700">{{ $t('fields.member.state') }}:</label>
        <select v-model="form.state" class="form-select mt-1 w-full">
          <option :value="null"/>
          <option v-for="state in states" :value="state">{{ state }}</option>
        </select>
      </search-filter>

      <Link v-if="time_to_start > 0" class="btn-indigo"
            :href="route('admin.elections.candidates.edit', election.uuid)">
        <span>{{ $t('actions.update_model_detail', [$t('models.candidate')]) }}</span>
      </Link>

      <Link v-if="time_to_end < 0" class="btn-indigo" @click.prevent="exportResults">
        <span>导出结果</span>
      </Link>
    </div>

    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
        <tr class="text-left font-bold">
          <th class="w-px"></th>
          <th class="pb-4 pt-6 px-4">{{ $t('fields.member.full_name_cn') }}</th>
          <th class="pb-4 pt-6 px-4">{{ $t('fields.member.membership_number') }}</th>
          <th class="pb-4 pt-6 px-4">{{ $t('fields.member.state') }}</th>
          <th class="pb-4 pt-6 px-4">{{ $t('fields.candidate.votes_count') }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="candidate in candidates" :key="candidate.uuid"
            class="hover:bg-gray-100 focus-within:bg-gray-100 cursor-pointer">
          <!--          <td class="px-2 py-4 border-t">-->
          <!--            <img v-if="candidate.photo_url" class="block -my-2 mx-2 w-8 h-8 rounded"-->
          <!--                 :src="candidate.photo_url" alt="election cover_image"/>-->
          <!--          </td>-->
          <td class="pl-2 pr-0 py-4 border-t">
            <img v-if="candidate.photo_url"
                 class="block -my-3 mx-2 h-10 w-auto rounded object-contain"
                 style="max-width: inherit;" :src="candidate.photo_url" alt="election cover_image"/>
          </td>
          <td class="px-4 py-4 border-t">
            {{ candidate.display_name }}
          </td>
          <td class="px-4 py-4 border-t">
            {{ candidate.membership_number }}
          </td>
          <td class="px-4 py-4 border-t">
            {{ candidate.state }}
          </td>
          <td class="px-4 py-4 border-t">
            {{ candidate.votes_count }}
          </td>
        </tr>
        <tr v-if="candidates.length === 0">
          <td class="px-4 py-4 border-t" colspan="4">{{ $t('alerts.no_record_found') }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Layouts/Authenticated'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import TextInput from "@/Inputs/TextInput";
import TextareaInput from "@/Inputs/TextareaInput";

export default {
  components: {
    TextareaInput,
    TextInput,
    Head,
    Icon,
    Link,
    SearchFilter,
  },
  layout: Layout,
  props: {
    filters: Object,
    election: Object,
    states: Array,
    candidates: Array,
    time_to_start: Number,
    time_to_end: Number,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        state: this.filters.state,
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(route('admin.elections.candidates.index', this.election.uuid), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    visit(url) {
      this.$inertia.get(url)
    },
    exportResults() {
      this.$inertia.post(route('admin.elections.candidates.results', this.election.uuid));
    },
  },
}
</script>
